"use client";

import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { ExternalLink, Calendar } from "lucide-react";
import config from "../../../config/config";
import { getFeaturedImage } from "../../../utils/imageUtils";

export default function TabsSection() {
  const [activeTab, setActiveTab] = useState("press-release");
  const [pressReleases, setPressReleases] = useState([]);
  const [articles, setArticles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Press Releases
        const pressResponse = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/p/press-release?limit=10`
        );
        if (pressResponse.ok) {
          const pressData = await pressResponse.json();
          if (pressData.success) {
            setPressReleases(pressData.data.posts || []);
          }
        }

        // Fetch Articles/Blog
        const articlesResponse = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/p/blog?limit=10`
        );
        if (articlesResponse.ok) {
          const articlesData = await articlesResponse.json();
          if (articlesData.success) {
            setArticles(articlesData.data.posts || []);
          }
        }

        // Fetch Photos/Gallery
        const photosResponse = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/p/gallery?limit=10`
        );
        if (photosResponse.ok) {
          const photosData = await photosResponse.json();
          if (photosData.success) {
            setPhotos(photosData.data.posts || []);
          }
        }
      } catch (error) {
        console.error("Error fetching tab data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getTabContent = () => {
    switch (activeTab) {
      case "articles":
        return articles;
      case "photos":
        return photos;
      default:
        return pressReleases;
    }
  };

  const formatDate = (dateString) => {
    console.log("TabsSection formatDate input:", dateString);
    if (!dateString) return "Date TBA";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.log("TabsSection Invalid date:", dateString);
      return "Date TBA";
    }

    const result = date.toLocaleDateString("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    console.log("TabsSection formatted result:", result);
    return result;
  };

  if (loading) {
    return (
      <div className="col-span-1 lg:col-span-1">
        <div className="bg-white border rounded-t-sm border-gray-200 overflow-hidden flex flex-col h-auto lg:h-[600px]">
          <div className="flex bg-primary h-10 sm:h-12 flex-shrink-0">
            <div className="flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold bg-primary/80 text-primary-foreground">
              PRESS RELEASE
            </div>
            <div className="flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold bg-primary/80 text-primary-foreground">
              ARTICLES
            </div>
            <div className="flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold bg-primary/80 text-primary-foreground">
              PHOTOS
            </div>
          </div>
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 flex-1 lg:overflow-hidden">
            <div className="animate-pulse space-y-3 sm:space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2 sm:space-y-3">
                  <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-1 lg:col-span-1">
      <div className="bg-white border rounded-t-sm border-gray-200 overflow-hidden flex flex-col h-auto lg:h-[600px]">
        {/* Tabs */}
        <div className="flex bg-primary h-10 sm:h-12 flex-shrink-0">
          <button
            onClick={() => setActiveTab("press-release")}
            className={`flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === "press-release"
                ? "bg-logoBlue text-white"
                : "bg-primary/80 text-primary-foreground hover:bg-primary/60"
            }`}
          >
            PRESS RELEASE
          </button>
          <button
            onClick={() => setActiveTab("articles")}
            className={`flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === "articles"
                ? "bg-logoBlue text-white"
                : "bg-primary/80 text-primary-foreground hover:bg-primary/60"
            }`}
          >
            ARTICLES
          </button>
          <button
            onClick={() => setActiveTab("photos")}
            className={`flex-1 h-full flex items-center justify-center px-1 sm:px-2 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === "photos"
                ? "bg-logoBlue text-white"
                : "bg-primary/80 text-primary-foreground hover:bg-primary/60"
            }`}
          >
            PHOTOS
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 flex-1 lg:overflow-y-auto">
          {activeTab === "photos" ? (
            // Special layout for photos - square images with overlay
            <div className="space-y-2 sm:space-y-3">
              {getTabContent().map((item, index) => (
                <div
                  key={item._id || item.id || index}
                  className="group cursor-pointer"
                  onClick={() => window.open(`/gallery/${item.slug}`, "_blank")}
                >
                  <div className="space-y-2">
                    {/* Square Photo Image with Overlay */}
                    <div className="w-full aspect-square bg-gray-200 rounded overflow-hidden relative">
                      <img
                        src={getFeaturedImage(item, "small")}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML =
                            '<div class="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500 text-xs">No Image</div>';
                        }}
                      />
                      {/* Overlay with gradient background */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

                      {/* Title and Date Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 text-white">
                        <h3 className="text-xs sm:text-sm font-semibold text-white mb-1 sm:mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        <div className="flex items-center text-xs text-white/90">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(
                            item.publishDate ||
                              item.publishedAt ||
                              item.createdAt
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {index < getTabContent().length - 1 && (
                    <hr className="mt-2" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Original layout for press-release and articles
            getTabContent().map((item, index) => (
              <div
                key={item._id || item.id || index}
                className="group cursor-pointer"
                onClick={() => {
                  const postTypeMap = {
                    "press-release": "press-release",
                    articles: "blog",
                    photos: "gallery",
                  };
                  window.open(
                    `/${postTypeMap[activeTab]}/${item.slug}`,
                    "_blank"
                  );
                }}
              >
                <div className="space-y-2 sm:space-y-3">
                  {/* Feature Image - Only show for first item */}
                  {index === 0 && (
                    <div className="w-full h-24 sm:h-28 md:h-32 bg-gray-200 rounded overflow-hidden">
                      <img
                        src={getFeaturedImage(item, "small")}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  <h3 className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-logoBlue transition-colors line-clamp-2 sm:line-clamp-3">
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(
                        item.publishDate || item.publishedAt || item.createdAt
                      )}
                    </span>
                    <div className="flex space-x-2">
                      <button className="social-icon-button w-6 h-6 bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors rounded-sm">
                        <span className="text-xs font-bold leading-none">
                          f
                        </span>
                      </button>
                      <button className="social-icon-button w-6 h-6 bg-blue-400 flex items-center justify-center text-white hover:bg-blue-500 transition-colors rounded-sm">
                        <span className="text-xs font-bold leading-none">
                          t
                        </span>
                      </button>
                      <button className="social-icon-button w-6 h-6 bg-blue-500 flex items-center justify-center text-white hover:bg-blue-600 transition-colors rounded-sm">
                        <span className="text-xs font-bold leading-none">
                          @
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="text-logoRed hover:text-logoRed/80 text-xs font-semibold">
                    Read More »
                  </div>
                </div>

                {index < getTabContent().length - 1 && (
                  <hr className="mt-3 sm:mt-4" />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
